<template>
  <PrimaryModal @clickBackground="onClose">
    <div
      class="modal-size-min max-w-screen-lg box-border px-8 pt-4 pb-8 m-4 text-gray-800 dark:text-dark-text"
      style="user-select: none"
    >
      <div class="flex mt-2 ml-4">
        <span class="material-icons my-auto text-2xl">reviews</span>
        <h1 class="text-2xl pl-1 border-gray-400 dark:border-dark-200">
          {{ $t('message.review') }}
        </h1>
      </div>
      <div
        v-if="reviewRequestPresenter.isLoading.value"
        class="py-12 bg-white dark:bg-dark-900"
        style="position: relative"
      >
        <div :class="isLightMode() ? 'loading' : 'loading-dark'" />
      </div>
      <div v-else class="mt-6">
        <div v-if="requestList != undefined" class="flex flex-col">
          <OutlinedIconTextButton
            v-for="(item, index) in requestList"
            :key="item.id"
            class="mx-2 my-2"
            :icon="checkedList[index] ? 'radio_button_checked' : 'radio_button_unchecked'"
            :iconSize="'text-2xl'"
            :padding_bottom="2"
            :padding_left="2"
            :padding_right="2"
            :padding_top="2"
            :style="{
              backgroundColor: getBackgroundColor(index, requestList.length)
            }"
            :text="item.message"
            @click="checkRequestItem(index)"
          />
        </div>
      </div>
      <div class="mt-4 flex items-center justify-center">
        <div class="text-base" style="width: 98%">
          <div
            class="border border-gray-300 bg-gray-200 dark:border-dark-400 dark:bg-dark-500"
            style="padding: 2px 12px 2px 12px; border-radius: 1rem 1rem 0 0"
          >
            <label class="text-gray-800 dark:text-dark-text">
              {{ $t('message.freeDescription') }}
            </label>
          </div>
          <div class="w-full">
            <InputArea
              class="shadow-md border border-gray-300 text-gray-800 dark:border-dark-400 dark:text-dark-text dark:bg-dark-400"
              :isEditable="true"
              :onChange="reviewRequestPresenter.setFreeDescription"
              style="height: 160px; border-radius: 0 0 1rem 1rem"
              :value="reviewRequestPresenter.freeDescription.value"
              :width="'100%'"
            />
          </div>
        </div>
      </div>
      <div
        class="px-4 flex mt-6 mb-2 text-red-600 dark:text-red-400"
        style="user-select: contain"
        v-html="aiAlertStr"
      />
      <div class="px-4 ml-6 flex mb-8" style="user-select: contain">
        <ul class="list-disc">
          <li>
            <a
              class="hover:underline text-blue-500"
              href="https://openai.com/ja-JP/policies/terms-of-use/"
              target="_blank"
            >
              ChatGPT
            </a>
          </li>
          <li>
            <a
              class="hover:underline text-blue-500"
              href="https://support.google.com/gemini/answer/13594961?hl=ja"
              target="_blank"
            >
              Gemini
            </a>
          </li>
        </ul>
      </div>
      <div class="flex items-center justify-center">
        <IconTextButton
          :border_radius="1"
          class="mr-2 mt-1 mb-4"
          :icon="'send'"
          :iconSize="'text-xl'"
          :padding_bottom="4"
          :padding_left="10"
          :padding_right="10"
          :padding_top="4"
          :text="$t('message.requestReview')"
          @click="requestReview()"
        />
      </div>
    </div>
  </PrimaryModal>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from 'vue'

import { convertJudgeStatusKey } from '@/domain/value_objects/judge/JudgeStatusType'
import IconTextButton from '@/presentation/components/Button/IconTextButton.vue'
import OutlinedIconTextButton from '@/presentation/components/Button/OutlinedIconTextButton.vue'
import InputArea from '@/presentation/components/FreeSpace/children/InputArea.vue'
import PrimaryModal from '@/presentation/components/Modal/PrimaryModal.vue'
import { useConfigStatePresenter } from '@/presentation/composables/config/useConfigStatePresenter'
import { useModalStatePresenter } from '@/presentation/composables/modal/useModalStatePresenter'
import { useNotificationPresenter } from '@/presentation/composables/notification/useNotificationPresenter'
import { useReviewRequestPresenter } from '@/presentation/composables/reviewRequest/useReviewRequestPresenter'

export default defineComponent({
  components: { PrimaryModal, IconTextButton, OutlinedIconTextButton, InputArea },
  setup(props, ctx) {
    const { displayLanguage, isLightMode } = useConfigStatePresenter()
    const reviewRequestPresenter = useReviewRequestPresenter()
    const notificationPresenter = useNotificationPresenter()
    const modalStatePresenter = useModalStatePresenter()

    reviewRequestPresenter.getReviewRequestList(
      convertJudgeStatusKey(reviewRequestPresenter.reviewJudgeStatus.value),
      displayLanguage.value
    )

    const requestList = computed(() => {
      const list = reviewRequestPresenter.reviewRequestList.value
      return list.length > 0 ? list : null
    })

    const checkedList = ref<boolean[]>([])

    watch(requestList, newList => {
      checkedList.value = newList ? newList.map(() => false) : []
    })

    const checkRequestItem = (index: number) => {
      const list = checkedList.value
      list.map((_, i) => {
        if (i != index) list[i] = false
      })
      list[index] = !list[index]
    }

    const requestReview = async () => {
      const list = requestList.value
      if (list == undefined) return
      const requests = checkedList.value
        .map((checked, index) => {
          if (checked) return list[index].id
        })
        .filter((id): id is string => id !== undefined)

      if (
        (requests.length >= 1 && reviewRequestPresenter.freeDescription.value != '') ||
        (requests.length <= 0 && reviewRequestPresenter.freeDescription.value == '')
      ) {
        notificationPresenter.invalidReviewNotification()
        return
      }

      const judgeId = reviewRequestPresenter.reviewJudgeId.value
      await reviewRequestPresenter.postRegisterEntry(
        judgeId,
        requests,
        reviewRequestPresenter.freeDescription.value,
        displayLanguage.value
      )
    }

    const getBackgroundColor = (index: number, max: number) => {
      const ratio = index / (max - 1)

      const startColor = isLightMode() ? { r: 201, g: 218, b: 248 } : { r: 32, g: 32, b: 96 } // C9DAF8
      const endColor = isLightMode() ? { r: 234, g: 209, b: 220 } : { r: 96, g: 32, b: 32 } // EAD1DC

      const red = Math.round(startColor.r + (endColor.r - startColor.r) * ratio)
      const green = Math.round(startColor.g + (endColor.g - startColor.g) * ratio)
      const blue = Math.round(startColor.b + (endColor.b - startColor.b) * ratio)

      return `rgb(${red}, ${green}, ${blue})`
    }

    const onClose = () => {
      modalStatePresenter.setIsShowReviewAlertModal(false)
    }

    const aiAlertStr =
      displayLanguage.value === 'Japanese'
        ? 'エントリーされたリクエストに対して、AIがコードレビューを行う場合があります。詳しくは下記の利用規約をご覧ください。'
        : 'For requests entered, AI may perform a code review. For more information, please see the Terms of Use below.'

    return {
      reviewRequestPresenter,
      onClose,
      requestReview,
      checkRequestItem,
      getBackgroundColor,
      isLightMode,
      requestList,
      checkedList,
      aiAlertStr
    }
  }
})
</script>

<style scoped>
.loading::after {
  content: '';
  position: absolute;
  width: 3em;
  height: 3em;
  top: 0px;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  border: 5px #d1d5db solid;
  border-top: 5px gray solid;
  border-radius: 50%;
  animation-name: circ;
  animation-delay: 0ms;
  animation-duration: 1000ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}

.loading-dark::after {
  content: '';
  position: absolute;
  width: 3em;
  height: 3em;
  top: 0px;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  border: 5px #1e1e1e solid;
  border-top: 5px lightgray solid;
  border-radius: 50%;
  animation-name: circ;
  animation-delay: 0ms;
  animation-duration: 1000ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}
</style>
